import * as React from "react";
import { useParams } from "react-router";

import { Container, Grid, Stack } from "@mui/material";

import LightbulbIcon from "@mui/icons-material/Lightbulb";
import RateReviewIcon from "@mui/icons-material/RateReview";

import CustomForm from "../../../helper-components/form/CustomForm.jsx";
import PaperFragment from "../../../helper-components/misc/PaperFragment.jsx";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter.jsx";
import PaperText from "../../../helper-components/misc/PaperText.jsx";
import CustomButton from "../../../helper-components/input/CustomButton.jsx";
import CustomInput from "../../../helper-components/input/CustomInput.jsx";

import HighlightReviewsTable from "./components/HighlightReviewsTable.jsx";

import {
    addFormToObj,
    formatText,
    convertSecondsToFormattedTime,
    createUploadObject,
    setInsertObjectState,
    formatDate,
    isEmpty,
    setValue,
    isYoutube,
    isVimeo,
    isAdmin
} from "../../../helper-functions/shared/miscUtils.js";
import { createHighlightReview, queryHighlight } from "../../../helper-functions/api/highlightQueries.js";

import { getLanguage } from "../../../config/language/language.js";
import { HIGHLIGHT_REVIEW_REQUEST_OBJECT } from "../../../config/constants/Constants.js";

/**
 * A page detailing a highlight.
 *
 * Includes:
 * - formatted highlight text
 * - quotes
 * - upvote / downvote functionality
 */
export default function HighlightView(props) {
    const { id } = useParams(); // Get the URL parameters from react-router.

    const [highlight, setHighlight] = React.useState({});
    const [highlightQuery, setHighlightQuery] = React.useState(false);

    const highlights = props?.backend?.highlights?.data;
    const highlightRequest = highlight?.highlight_request;
    const resource = highlight?.resource;

    const isUserAdmin = isAdmin(props);
    const isReady = !isEmpty(highlight) && !isEmpty(highlight?.text);

    const [userHasVoted, setUserHasVoted] = React.useState(true);
    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(HIGHLIGHT_REVIEW_REQUEST_OBJECT))));

    // Reset the states when the component mounts or the id changes.
    React.useEffect(() => {
        if (!id) return;

        setHighlight({});
        setHighlightQuery(false);
        setUserHasVoted(true);
        setRow(addFormToObj(JSON.parse(JSON.stringify(HIGHLIGHT_REVIEW_REQUEST_OBJECT))));
    }, [id]);

    // Set the highlight, and potentially query it if it's not available.
    React.useEffect(() => {
        const possibleHighlight = highlights.find((h) => h.id === parseInt(id));

        if (!isEmpty(possibleHighlight)) {
            setHighlightQuery(true);
            setHighlight(possibleHighlight);

            // Misc. state updates.
            checkUserVote(possibleHighlight);
        }

        if (isEmpty(possibleHighlight) && !highlightQuery) {
            setHighlightQuery(true);
            queryHighlight(props, { id });
        }
    }, [highlights, highlight]);

    const createEmbedUrl = () => {
        if (!resource?.url) return "";

        if (isYoutube(resource?.url)) {
            return resource?.url?.replace("watch?v=", "embed/") + "?start=" + parseInt(highlight?.meta_data?.start ?? 0);
        }

        if (isVimeo(resource?.url)) {
            return resource.url + "#t=" + parseInt(highlight?.meta_data?.start ?? 0);
        }

        return resource.url;
    };

    const createVideoUrl = () => {
        if (!resource?.url) return "";

        if (isYoutube(resource?.url)) {
            return resource?.url + "&t=" + parseInt(highlight?.meta_data?.start ?? 0);
        }

        if (isVimeo(resource?.url)) {
            return resource.url + "#t=" + parseInt(highlight?.meta_data?.start ?? 0);
        }

        return resource.url;
    };

    const checkUserVote = (highlight) => {
        const user = props.user.me;
        const reviews = JSON.parse(JSON.stringify(highlight?.highlight_reviews || []));

        // Also check if the user has already voted on this highlight.
        if (user) {
            const userVote = reviews.find((review) => review?.user_id === user.id);
            setUserHasVoted(!isEmpty(userVote));
        }
    };

    const createHighlightDetails = () => {
        const text = `
        Status: ${formatText(highlight?.status)}
        Resource uploaded on: ${formatDate(resource?.uploaded_on, { onlyDate: true }) ?? "N/A"}

        Start: ${highlight?.meta_data?.start ? convertSecondsToFormattedTime(highlight?.meta_data?.start) : "N/A"}
        Duration: ${highlight?.meta_data?.duration ? convertSecondsToFormattedTime(highlight?.meta_data?.duration) : "N/A"}
        `;

        return text.trim();
    };

    const createHighlightRequestDetails = () => {
        const text = `
        Name: ${highlightRequest?.name ?? "N/A"}
        Description: ${highlightRequest?.description ?? "N/A"}
        `;

        return text.trim();
    };

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        setValue(newRow, key, value);

        setInsertObjectState(setRow, newRow);
    };

    const handleClick = (vote) => {
        const body = createUploadObject(row, ["form"]);

        body.vote = vote;
        body.highlight_id = highlight?.id;

        const reviewQueue = props.backend.highlightReviewQueue || [];

        createHighlightReview(props, { body }, () => {
            const nextIndex = reviewQueue.findIndex((h) => h?.id === highlight?.id) + 1;
            const nextHighlight = reviewQueue?.[nextIndex];

            // Move to next highlight if available.
            if (nextHighlight) {
                props.browser.navigate(`/highlights/view/${nextHighlight.id}`);
            } else {
                // Check if highlight id is the last one in the queue
                if (reviewQueue.length > 0 && reviewQueue?.[reviewQueue.length - 1]?.id === highlight?.id) {
                    props.backend.setHighlightReviewQueue(null);
                    props.showSnackbar({ message: "Review process completed! Feel free to begin again.", color: "success", duration: 7500 });
                }

                props.browser.navigate("/highlights/view");
            }
        });
    };

    const createIframe = () => {
        if (!isYoutube(resource?.url) && !isVimeo(resource?.url)) return;

        return (
            <PaperFragment
                p={1.5}
                value={
                    <>
                        <iframe
                            src={createEmbedUrl()}
                            title="Video player"
                            style={{ width: "100%", height: "100%", minHeight: "250px", marginBottom: "5px" }}
                            allowFullScreen
                            referrerPolicy="strict-origin-when-cross-origin"></iframe>

                        <CustomButton
                            {...props}
                            title="Open resource URL"
                            size="small"
                            onClick={() => window.open(createVideoUrl(), "_blank")}
                            disabled={!resource?.url}
                            variant="outlined"
                            fullWidth
                        />
                    </>
                }
            />
        );
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <CustomForm title={getLanguage().HIGHLIGHT_REVIEW_TITLE} subtitle={getLanguage().HIGHLIGHT_REVIEW_SUBTITLE} icon={<RateReviewIcon />} />
                </Grid>

                {highlightQuery && !isReady && (
                    <Grid item xs={12}>
                        <PaperText value={getLanguage().NO_HIGHLIGHT_FOUND} />
                    </Grid>
                )}

                {isReady && (
                    <>
                        <Grid item xs={12} md={7}>
                            <PaperText bold center value={resource?.name || "Title: N/A"} />
                            <PaperText p={1.5} whiteSpace="pre-line" fontStyle="italic" value={highlight?.text ?? "N/A"} />
                            {highlight?.text?.length < 2000 && createIframe()}
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <PaperText bold center value={`Highlight Details`} />
                            <PaperText p={1.5} whiteSpace="pre-line" value={createHighlightDetails()} />

                            <PaperText bold center value={`Highlight Request Details`} />
                            <PaperText p={1.5} whiteSpace="pre-line" value={createHighlightRequestDetails()} />

                            <PaperText bold center value={`Highlight Review`} />
                            <PaperFragment
                                p={1.5}
                                value={
                                    <>
                                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
                                            <CustomButton
                                                {...props}
                                                title="No"
                                                size="medium"
                                                onClick={() => handleClick("NO")}
                                                disabled={highlight?.status !== "AWAITING_APPROVAL" || userHasVoted}
                                                variant="contained"
                                                color="error"
                                                fullWidth
                                            />
                                            <CustomButton
                                                {...props}
                                                title="Maybe"
                                                size="medium"
                                                onClick={() => handleClick("MAYBE")}
                                                disabled={highlight?.status !== "AWAITING_APPROVAL" || userHasVoted || isUserAdmin}
                                                variant="contained"
                                                color="warning"
                                                fullWidth
                                            />
                                            <CustomButton
                                                {...props}
                                                title="Yes"
                                                size="medium"
                                                onClick={() => handleClick("YES")}
                                                disabled={highlight?.status !== "AWAITING_APPROVAL" || userHasVoted}
                                                variant="contained"
                                                color="success"
                                                fullWidth
                                            />
                                        </Stack>

                                        <Grid item xs={12}>
                                            <CustomInput
                                                name="note"
                                                row={row}
                                                onChange={onChange}
                                                label="Comment"
                                                multiline
                                                minRows={3}
                                                maxRows={5}
                                                disabled={highlight?.status !== "AWAITING_APPROVAL" || userHasVoted}
                                            />
                                        </Grid>
                                    </>
                                }
                            />

                            {highlight?.text?.length >= 2000 && createIframe()}
                        </Grid>

                        <Grid item xs={12}>
                            <HighlightReviewsTable {...props} highlight={highlight} />
                        </Grid>
                    </>
                )}

                <NavigationFooter
                    rows={1}
                    options={[
                        {
                            path: `/highlights/view`,
                            title: "Back to highlights",
                            variant: "outlined",
                            icon: <RateReviewIcon />
                        },
                        {
                            path: `/highlights/requests`,
                            title: "Highlight requests",
                            variant: "outlined",
                            icon: <LightbulbIcon />
                        }
                    ]}
                />
            </Grid>
        </Container>
    );
}
